import type { Project } from '@atlassian/jira-common-constants/src/project-types';
import {
	ROUTE_NAMES_RAPIDBOARD_USER_BOARD,
	ROUTE_NAMES_RAPIDBOARD_USER_BACKLOG,
	ROUTE_NAMES_RAPIDBOARD_USER_REPORT,
} from '@atlassian/jira-common-constants/src/spa-routes';
import { useBoard } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils';
import { usePathParam } from '@atlassian/react-resource-router';

export const useThrowProject = withThrowOnError(useProject);
export const useThrowBoard = withThrowOnError(useBoard);

export const getProjectUrl = ({
	project,
	projectPluginPage,
	path = '',
	cmpPath = '',
	tmpPath = '',
}: {
	project?: Project;
	projectPluginPage?: string;
	/** A path to append to the generated project url */
	path?: string;
	/** A path to append to the generated project url for CMP projects only (overrides path) */
	cmpPath?: string;
	/** A path to append to the generated project url for TMP projects only (overrides path) */
	tmpPath?: string;
} = {}) => {
	if (projectPluginPage) {
		return `/projects/${project?.key}?selectedItem=com.atlassian.jira.jira-projects-plugin:${projectPluginPage}`;
	}
	if (project?.isSimplified) {
		if (tmpPath) return `/jira/software/projects/${project?.key}${tmpPath}`;
		return `/jira/software/projects/${project?.key}${path}`;
	}
	if (cmpPath) return `/jira/software/c/projects/${project?.key}${cmpPath}`;
	return `/jira/software/c/projects/${project?.key}${path}`;
};
export const useProjectUrl = (
	params: {
		projectPluginPage?: string;
		path?: string;
		cmpPath?: string;
		tmpPath?: string;
	} = {},
) => {
	const { data: project } = useThrowProject();
	return project ? getProjectUrl({ project, ...params }) : '';
};

export const useBoardsUrl = (path: string) => {
	const { data: board } = useThrowBoard();
	const { data: project } = useProject();
	const isUserBoard = useIsUserBoard();
	const [accountId] = usePathParam('accountId');

	if (isUserBoard) {
		return `/jira/people/${accountId ?? ''}/boards/${board?.id}${path}`;
	}

	return project ? getProjectUrl({ project, path: `/boards/${board?.id}${path}` }) : '';
};

export const useIsUserBoard = () => {
	const { name } = useCurrentRoute();
	return [
		`${ROUTE_NAMES_RAPIDBOARD_USER_BOARD}`,
		`${ROUTE_NAMES_RAPIDBOARD_USER_BACKLOG}`,
		`${ROUTE_NAMES_RAPIDBOARD_USER_REPORT}`,
	].includes(name);
};
